/** User-facing message when a profile link URL fails validation. */
export const PROFILE_LINK_URL_ERROR =
  'Enter a full URL with http:// or https:// and a domain (e.g. https://example.com).';

export const PROFILE_LINK_TITLE_ERROR = 'Title is required for each link.';
export const PROFILE_LINK_URL_REQUIRED_ERROR = 'URL is required when a title is provided.';
export const PROFILE_LINK_DUPLICATE_ERROR = 'This URL is already added.';

const HOST_LABEL_RE = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/i;

/** Trim and ensure an http(s) scheme. Does not validate. */
export function normalizeProfileLinkUrl(url) {
  const trimmed = (url || '').trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

function isValidProfileLinkHostname(host) {
  const h = (host || '').toLowerCase();
  if (!h) return false;
  if (h === 'localhost') return true;
  if (/^127\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(h)) return true;

  const parts = h.split('.').filter(Boolean);
  if (parts.length < 2) return false;

  const tld = parts[parts.length - 1];
  if (!/^[a-z]{2,63}$/i.test(tld)) return false;

  return parts.every((label) => HOST_LABEL_RE.test(label));
}

/**
 * Validate a single profile-link URL after optional normalization.
 * Empty input is valid (caller skips the row).
 */
export function validateProfileLinkUrl(raw) {
  const trimmed = (raw || '').trim();
  if (!trimmed) return { ok: true, url: '' };

  if (/\s/.test(trimmed)) {
    return { ok: false, error: PROFILE_LINK_URL_ERROR };
  }

  // Block dangerous schemes before we prepend https://.
  if (/^(javascript|data|vbscript|file|mailto):/i.test(trimmed)) {
    return { ok: false, error: PROFILE_LINK_URL_ERROR };
  }

  const url = normalizeProfileLinkUrl(trimmed);

  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    return { ok: false, error: PROFILE_LINK_URL_ERROR };
  }

  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return { ok: false, error: PROFILE_LINK_URL_ERROR };
  }
  if (parsed.username || parsed.password) {
    return { ok: false, error: PROFILE_LINK_URL_ERROR };
  }
  if (!isValidProfileLinkHostname(parsed.hostname)) {
    return { ok: false, error: PROFILE_LINK_URL_ERROR };
  }
  if (url.length > 500) {
    return { ok: false, error: 'URL must be 500 characters or fewer.' };
  }

  return { ok: true, url };
}

/**
 * Validate all modal rows. Returns cleaned links plus per-field errors keyed
 * by row index.
 */
export function validateProfileLinkRows(rows) {
  const urlErrors = {};
  const titleErrors = {};
  const cleaned = [];
  const seenUrls = new Set();

  (rows || []).forEach((row, index) => {
    const title = (row?.title || '').trim();
    const rawUrl = (row?.url || '').trim();

    if (!title && !rawUrl) return;

    if (title && !rawUrl) {
      urlErrors[index] = PROFILE_LINK_URL_REQUIRED_ERROR;
      return;
    }

    const urlResult = validateProfileLinkUrl(rawUrl);
    if (!urlResult.ok) {
      urlErrors[index] = urlResult.error;
      return;
    }

    if (!title) {
      titleErrors[index] = PROFILE_LINK_TITLE_ERROR;
      return;
    }

    const dedupeKey = urlResult.url.toLowerCase();
    if (seenUrls.has(dedupeKey)) {
      urlErrors[index] = PROFILE_LINK_DUPLICATE_ERROR;
      return;
    }
    seenUrls.add(dedupeKey);
    cleaned.push({ title: title.slice(0, 60), url: urlResult.url });
  });

  return { urlErrors, titleErrors, cleaned };
}
